import { Component, OnInit, signal, computed, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { IngestionPoint } from "../data-ingestion/data.ingestion.service";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";

@TypeManagerDecorator("workflow-init-rules")
@Component({
    selector: "workflow-init-rules",
    template: `
        <div class="workflow-init-rules">
            <div class="page-header">
                <h1>Workflow Initialization Rules</h1>
                <p class="page-description">
                    Configure rules to automatically assign workflow processes to form submissions based on form field
                    values.
                </p>
            </div>

            <div class="form-selector-section">
                <div class="form-group">
                    <label for="formSelector">Select Form:</label>
                    <div class="form-control-container">
                        @if (showFormDropdown()) {
                            <s25-ingestion-point-dropdown
                                [(value)]="selectedFormDropdownItem"
                                [enabledOnly]="true"
                                (valueChange)="onFormSelected($event)"
                                class="form-dropdown"
                            />
                        }
                        <s25-ng-button
                            type="primary"
                            buttonText="Refresh"
                            [onClick]="refreshForms"
                            buttonClass="refresh-btn"
                        ></s25-ng-button>
                    </div>
                </div>
            </div>

            @if (selectedFormId()) {
                <div class="rules-editor">
                    <div class="editor-header">
                        <h2>Workflow Rules for: {{ getSelectedFormName() }}</h2>
                        <p>Define rules to automatically assign workflows based on form field values.</p>
                    </div>

                    <div class="rule-tree-container">
                        <s25-ng-rule-tree
                            [category]="'workflowInit'"
                            [subCategory]="selectedFormId()!.toString()"
                            [conditionFilterMap]="workflowInitConditionFilterMap()"
                        ></s25-ng-rule-tree>
                    </div>
                </div>
            } @else {
                <div class="no-selection">
                    <div class="no-selection-content">
                        <h3>Select a Form</h3>
                        <p>Choose a form from the dropdown above to configure its workflow initialization rules.</p>
                        <div class="help-text">
                            <h4>How Workflow Initialization Works:</h4>
                            <ol>
                                <li><strong>Form Submission:</strong> User submits a form</li>
                                <li><strong>Rule Evaluation:</strong> System checks conditional rules</li>
                                <li><strong>Workflow Assignment:</strong> Assigns matching workflow process</li>
                                <li><strong>Process Start:</strong> Workflow begins with initial state and assignee</li>
                            </ol>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .workflow-init-rules {
            padding: 20px;
            max-width: 1000px;
            margin: 0 auto;
        }

        .page-header {
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid var(--color--border);
        }

        .page-header h1 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .page-description {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .form-selector-section {
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
        }

        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text);
        }

        .form-control-container {
            display: flex;
            align-items: center;
            gap: 8px;
            max-width: 400px;
        }

        .form-control {
            flex: 1;
            padding: 10px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-control:focus {
            outline: none;
            border-color: var(--color--link-text);
            box-shadow: var(--shadow--form-focus);
        }

        .refresh-btn {
            min-width: 40px;
            height: 40px;
        }

        .form-dropdown {
            flex: 1;
        }

        .refresh-btn i {
            font-size: 16px;
        }

        .rules-editor {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .editor-header {
            background: var(--color--surface-2-bg);
            padding: 20px;
            border-bottom: 1px solid var(--color--border);
        }

        .editor-header h2 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .editor-header p {
            margin: 0 0 12px 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .form-info {
            background: var(--color--surface-2-bg);
            padding: 8px 12px;
            border-radius: 4px;
            font-size: 12px;
            color: var(--color--link-text);
        }

        .rule-tree-container {
            padding: 20px;
        }

        .no-selection {
            text-align: center;
            padding: 60px 20px;
        }

        .no-selection-content h3 {
            color: var(--color--text-strong);
            margin-bottom: 12px;
        }

        .no-selection-content p {
            color: var(--color--text-muted);
            margin-bottom: 24px;
        }

        .help-text {
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 20px;
            text-align: left;
            max-width: 500px;
            margin: 0 auto;
        }

        .help-text h4 {
            color: var(--color--text-strong);
            margin: 0 0 12px 0;
        }

        .help-text ol {
            margin: 0;
            color: var(--color--text-muted);
        }

        .help-text li {
            margin-bottom: 8px;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25RuleTreeModule, S25ButtonComponent, S25DropdownPaginatedModule],
})
export class WorkflowInitRulesComponent implements OnInit {
    selectedFormId = signal<number | null>(null);
    selectedFormDropdownItem: DropDownItem | null = null;
    showFormDropdown = signal(true);

    constructor(private cd: ChangeDetectorRef) {}

    // Condition filter map for workflowInit rules - uses ingestion fields for the selected form
    workflowInitConditionFilterMap = computed(() => {
        if (!this.selectedFormId()) return {};
        return {
            ingestionFieldsAll: "" + this.selectedFormId(),
        };
    });

    ngOnInit() {}

    refreshForms = async () => {
        // Destroy and recreate the dropdown by toggling the @if variable
        this.showFormDropdown.set(false);
        this.cd.detectChanges();
        this.showFormDropdown.set(true);
        this.cd.detectChanges();
    };

    onFormSelected(dropdownItem: DropDownItem | null | undefined): void {
        this.selectedFormDropdownItem = dropdownItem || null;
        this.selectedFormId.set(dropdownItem?.itemId ? Number(dropdownItem.itemId) : null);
    }

    getSelectedFormName(): string {
        if (!this.selectedFormId()) return "";
        const form = this.selectedFormDropdownItem?.data as IngestionPoint;
        return form?.ingestionPointName || `Form ${this.selectedFormId()}`;
    }
}
